import * as React from "react"
import { useEffect } from "react";
import {Skeleton, Grid, Box} from "@mui/material";

import { TitleWithPopover, AppTableSeeker } from "@components";
import {useToast} from "@hooks";
import { usePricingMarketTableData } from "./@hooks";
import { resetPricingMarketTable } from "./@signals";
import {
	competitorColumn,
	imageSkuColumn,
	priceColumn,
	skuColumn,
	countryColumn,
	numCompetitorsColumn,
	statusColumn,
	stockColumn,
	priceChangesColumn,
	skuRetailerColumn,
	graphColumn,
	dropColumn,
	discountColumn,
	unifiedPriceColumn,
	buyboxWonColumn
} from "./columns";
import PricingMarketComparisonActions from "./Actions";
import PricingMarketTableWarningLimit from "./WarningLimit";
import { useMarketAnalyzerRoleConfig } from "../../@hooks";
import PricingMarketAnalyzerTableTabs from "./Tabs";
import CreateReportButton from "./actions/CreateReportButton";
import {DISPLAY_TABS, RETAILER_DISPLAY_TABS} from "./@constants";


export default function PricingMarketAnalyzerTable() {
	const { isLoading, currentData, selectedCountry, handleSelectedCountry, countries, exceedLimit, displayTable, handleChangeTabs } = usePricingMarketTableData()
	const { isRetailerUser, isStandardUser, urlApiTable, userUseRRSP } = useMarketAnalyzerRoleConfig()
	const { showToast } = useToast()

	let isActual = displayTable === (isRetailerUser ? RETAILER_DISPLAY_TABS.CURRENT_PRICE : DISPLAY_TABS.ACTUAL_PRICE)

	const avgDict = {
		title: 'Avg:',
		priceKey: isActual ? 'avgPrice' : 'avgListPrice',
		diffKey: isActual ? 'avgDiff' : 'avgListDiff',
		retailerKey: null,
		stockKey: '',
		siteIDKey: '',
		productIDKey: null,
		showStock: false,
		numVariants: 'avgNumVariants'
	}
	const minDict = {
		title: 'Min:',
		priceKey: isActual ? 'minPrice' : 'minListPrice',
		diffKey: isActual ? 'minDiff': 'minListDiff',
		retailerKey: isActual ? 'minRetailer': 'minListRetailer',
		stockKey: isActual ? 'minPriceStock': 'minListPriceStock',
		siteIDKey: isActual ? 'minIdSite': 'minListIdSite',
		productIDKey: isActual ? 'minProductId': 'minListProductId',
		showStock: true,
		numVariants: isActual ? 'minSiteNumVariants' : 'minSiteListNumVariants'
	}
	const maxDict = {
		title: 'Max:',
		priceKey: isActual ? 'maxPrice': 'maxListPrice',
		diffKey: isActual ? 'maxDiff': 'maxListDiff',
		retailerKey: isActual ? 'maxRetailer': 'maxListRetailer',
		stockKey: '',
		siteIDKey: isActual ? 'maxIdSite': 'maxListIdSite',
		productIDKey: isActual ? 'maxProductId': 'maxListProductId',
		showStock: false,
		numVariants: isActual ? 'maxSiteNumVariants' : 'maxSiteListNumVariants'
	}

	useEffect(() => {
        return () => resetPricingMarketTable()
    }, [])

	if (isLoading) return <Skeleton variant="rectangular" height={816} />
	if (!currentData.length) return <></>

	return <Grid container spacing={2}>

		<Grid item xs={12}>
			{ exceedLimit && <PricingMarketTableWarningLimit/>}
		</Grid>

		<Grid item xs={12}>
			<PricingMarketAnalyzerTableTabs
				value={displayTable}
				onChange={handleChangeTabs}
        	/>
			<AppTableSeeker
				title={ <TitleWithPopover title="Market Analyzer" />}
				data={currentData}
				columns={[
					...countryColumn(),
					imageSkuColumn(),
					...isStandardUser ? [...skuColumn()] : [],
					...isRetailerUser ? [...skuRetailerColumn()] : [],
					...isRetailerUser ? [priceColumn(isActual ? 'myActualPrice': 'myListPrice', isActual ? 'My Min Current Price' : 'My Min List Price', true, 'myStockCount', 150, true)] : [],
					...isStandardUser && userUseRRSP ? [priceColumn('myPrice', 'RRSP', false, 'myStockCount',150)] : [],
					numCompetitorsColumn(),
					...userUseRRSP ? [statusColumn(!!isActual)] : [],
					...userUseRRSP ? [discountColumn()] : [],
					unifiedPriceColumn({
						label: 'Prices',
						priceComponents: [avgDict, minDict, maxDict]
					}),
					...isRetailerUser ? [stockColumn('myStock', 'My Stock')] : [],
					...isRetailerUser ? [stockColumn('stockCompetition', 'Stock Competition')] : [],
					...userUseRRSP ? [graphColumn(isActual ? 'statusPercentages' : 'statusListPercentages', isActual ? 'Status Share': 'Status List Share')] : [],
					...dropColumn(isActual ? 'firstDrop' : 'firstDropList', 'First Drop'),
					...dropColumn(isActual ? 'secondDrop' : 'secondDropList', 'Second Drop'),
					...isStandardUser ? [stockColumn('stock', 'Stock')] : [],
					...isStandardUser ? [buyboxWonColumn()] : [],
					priceChangesColumn(isActual ? 'priceChanges' : 'priceListChanges', isActual ? 'Price Changes' : 'Price List Changes'),
				]}
				options={{
					onDownload: () => showToast.promiseExcel(`${urlApiTable}/market-comparison/excel`),
					print: false,
					viewColumns: false,
					customToolbar: () => {
						return <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
							<PricingMarketComparisonActions
								selectedCountry={selectedCountry}
								countries={countries}
								onClick={handleSelectedCountry}
							/>
							<CreateReportButton />
						</Box>
					}
				}}
			/>
		</Grid>
		
	</Grid>
	

}